import React from "react";

/**
 * Top-level ErrorBoundary.
 *
 * React's default behaviour when any component throws during render is to
 * unmount the *entire* React tree. To the user this looks like the site has
 * gone completely blank — header, footer, content, everything vanishes.
 *
 * This boundary catches those errors and renders a recoverable UI so users
 * have a path forward (reload, go home, contact us) instead of a blank page.
 *
 * Usage: wrap <App /> in <ErrorBoundary>.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface to console for production debugging via DevTools.
    // Wire to Sentry / posthog here when we add error monitoring.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-6">
        <div className="max-w-md w-full" data-testid="error-boundary">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm border border-white/10 bg-white/5 text-[10.5px] uppercase tracking-[0.22em] text-white/65 font-mono mb-6">
            <span className="size-1.5 rounded-full bg-rose-400" /> Something went wrong
          </div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-semibold leading-tight">
            That page hit a snag.
          </h1>
          <p className="mt-4 text-[14.5px] text-white/65 leading-relaxed">
            We've logged the error. The fastest fix is usually a quick reload — your data is safe and nothing's been lost.
          </p>

          <div className="mt-7 flex flex-wrap gap-3">
            <button
              onClick={this.handleReload}
              data-testid="error-boundary-reload"
              className="inline-flex items-center gap-2 rounded-full bg-white text-slate-950 hover:bg-white/90 font-semibold px-5 h-10 text-[14px] transition-colors"
            >
              Reload page
            </button>
            <button
              onClick={this.handleGoHome}
              data-testid="error-boundary-home"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-transparent text-white hover:bg-white/5 font-medium px-5 h-10 text-[14px] transition-colors"
            >
              Go to homepage
            </button>
          </div>

          <div className="mt-10 pt-6 border-t border-white/10 text-[12px] text-white/45">
            If this keeps happening, email{" "}
            <a href="mailto:ops@connex.is" className="underline text-white/65 hover:text-white">
              ops@connex.is
            </a>{" "}
            and we'll fix it fast.
          </div>

          {process.env.NODE_ENV !== "production" && this.state.error && (
            <pre className="mt-6 p-3 rounded-sm bg-white/5 border border-white/10 text-[11px] text-white/55 font-mono overflow-x-auto">
              {String(this.state.error?.message || this.state.error)}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
